import { useEffect, useState } from 'react';
import {
  X,
  Monitor,
  Tablet,
  Smartphone,
  RotateCw,
  ExternalLink,
  Loader2,
  TriangleAlert,
} from 'lucide-react';

export type PreviewDevice = 'desktop' | 'tablet' | 'mobile';

interface PreviewModalProps {
  kind: 'post' | 'email';
  /** Document title shown in the preview header. */
  title: string;
  /** Persists the latest editor state and returns the fully rendered HTML. */
  onBuild: () => Promise<string>;
  onClose: () => void;
}

const DEVICES: { id: PreviewDevice; label: string; width: number; icon: typeof Monitor }[] = [
  { id: 'desktop', label: 'Desktop', width: 1280, icon: Monitor },
  { id: 'tablet', label: 'Tablet', width: 820, icon: Tablet },
  { id: 'mobile', label: 'Mobile', width: 390, icon: Smartphone },
];

const ICONS = { post: Monitor, email: Tablet };

/**
 * Fullscreen, device-aware preview of the server-rendered output. Runs the
 * same theme build the publish/send pipeline uses, so what you see here is
 * what readers get — drafts included.
 */
export function PreviewModal({ kind, title, onBuild, onClose }: PreviewModalProps) {
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const [html, setHtml] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');

  const load = () => {
    setState('loading');
    setError('');
    onBuild()
      .then((h) => {
        setHtml(h);
        setState('ready');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
        setState('error');
      });
  };

  useEffect(() => {
    load();
    // Build only on open; Refresh re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openInTab = () => {
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  const KindIcon = ICONS[kind];
  const activeWidth = DEVICES.find((d) => d.id === device)?.width ?? 1280;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-bg">
      {/* ── Header ─────────────────────────────────────────── */}
      <header className="flex h-14 shrink-0 items-center gap-2.5 border-b border-line bg-bg-soft/80 px-3 backdrop-blur-md sm:px-5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
          <KindIcon size={15} />
        </span>
        <div className="min-w-0">
          <p className="truncate text-[13px] font-bold tracking-tight text-ink capitalize">
            {kind} preview
          </p>
          <p className="truncate text-[11px] text-muted">{title || `Untitled ${kind}`}</p>
        </div>

        {/* Device toggle */}
        <div className="ml-auto flex items-center gap-0.5 rounded-full border border-line bg-bg p-1">
          {DEVICES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setDevice(id)}
              title={label}
              className={`flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold transition-all ${
                device === id ? 'bg-card text-ink shadow-sm' : 'text-muted hover:text-ink-soft'
              }`}
            >
              <Icon size={12} />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        <span className="mx-1 hidden h-5 w-px bg-line sm:block" />

        <button
          type="button"
          onClick={load}
          disabled={state === 'loading'}
          title="Rebuild preview"
          className="btn-ghost h-8 w-8 rounded-lg px-0 disabled:opacity-40"
        >
          <RotateCw size={14} className={state === 'loading' ? 'animate-spin' : ''} />
        </button>
        {html && (
          <button
            type="button"
            onClick={openInTab}
            title="Open in new tab"
            className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs"
          >
            <ExternalLink size={13} />
            <span className="hidden sm:inline">Open</span>
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Close preview"
          className="btn-ghost h-8 w-8 rounded-lg px-0"
        >
          <X size={15} />
        </button>
      </header>

      {/* ── Stage ──────────────────────────────────────────── */}
      <div className="relative flex-1 overflow-auto bg-bg">
        {state === 'loading' && (
          <div className="absolute inset-0 z-10 flex items-center justify-center gap-2.5 bg-bg/70 text-sm text-muted backdrop-blur-[2px]">
            <Loader2 size={16} className="animate-spin text-accent" />
            Rendering through the theme engine…
          </div>
        )}

        {state === 'error' && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-danger/10 text-danger">
              <TriangleAlert size={22} />
            </span>
            <p className="text-sm font-semibold text-ink">Couldn't build the preview</p>
            <p className="max-w-sm text-xs leading-relaxed text-muted">{error}</p>
            <button type="button" onClick={load} className="btn-secondary mt-2 text-xs">
              Try again
            </button>
          </div>
        )}

        {state === 'ready' && html && (
          <div className="flex min-h-full w-full items-start justify-center px-2 py-4 sm:px-6">
            <div className="flex w-full flex-col items-center">
              <div
                className={`w-full overflow-hidden rounded-xl border border-line bg-bg-soft shadow-lift transition-[max-width] duration-300 ease-out ${
                  device === 'mobile' ? 'max-w-[390px]' : device === 'tablet' ? 'max-w-[820px]' : 'max-w-[1280px]'
                }`}
              >
                <iframe
                  title={`${kind} preview`}
                  srcDoc={html}
                  sandbox="allow-same-origin allow-scripts allow-popups"
                  className="h-[calc(100dvh_-_3.5rem_-_2rem)] w-full border-0 bg-bg-soft"
                />
              </div>
              <p className="mt-2 pb-1 text-[11px] text-muted">
                {activeWidth}px viewport · rendered {kind === 'post' ? 'like the published blog' : 'like the sent email'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
